/* eslint-disable arrow-body-style */
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  collection,
  doc,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getDoc,
} from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import {
  OrganizerFollower,
  FollowerStats,
  FollowerListResponse,
  FollowerDashboardData,
} from 'src/@types/followers';

export const followersApi = createApi({
  reducerPath: 'followersApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Followers', 'FollowerStats'],
  endpoints: (builder) => ({
    // Get followers for an organizer (paginated)
    getFollowers: builder.query<
      FollowerListResponse,
      { organizerId: string; limitCount?: number; lastVisible?: any }
    >({
      queryFn: async ({ organizerId, limitCount = 50, lastVisible }) => {
        try {
          const followersRef = collection(DB, 'organisers', organizerId, 'followers');
          let q = query(followersRef, orderBy('followedAt', 'desc'), limit(limitCount));

          if (lastVisible) {
            q = query(q, startAfter(lastVisible));
          }

          const snapshot = await getDocs(q);
          const followers = snapshot.docs.map((doc) => ({
            ...doc.data(),
            userId: doc.id,
            followedAt: doc.data().followedAt,
          })) as OrganizerFollower[];

          // Fetch user details for followers that don't have userDisplayName
          const followersWithUserData = await Promise.all(
            followers.map(async (follower) => {
              if (!follower.userDisplayName && follower.userId) {
                try {
                  const userRef = doc(DB, 'users', follower.userId);
                  const userDoc = await getDoc(userRef);
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    return {
                      ...follower,
                      userDisplayName: userData.displayName || userData.firstName || userData.email,
                      userEmail: userData.email,
                      userPhoto: userData.photoURL,
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user ${follower.userId}:`, error);
                }
              }
              return follower;
            })
          );

          return {
            data: {
              followers: followersWithUserData,
              total: snapshot.size,
              hasMore: snapshot.size === limitCount,
              lastVisible: snapshot.docs[snapshot.docs.length - 1],
            },
          };
        } catch (error: any) {
          console.error('Error fetching followers:', error);
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, { organizerId }) => [
        { type: 'Followers', id: organizerId },
      ],
    }),

    // Get follower statistics
    getFollowerStats: builder.query<FollowerStats, string>({
      queryFn: async (organizerId) => {
        try {
          const followersRef = collection(DB, 'organisers', organizerId, 'followers');
          const snapshot = await getDocs(followersRef);

          const now = new Date();
          const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());

          let newFollowersThisMonth = 0;
          let newFollowersLastMonth = 0;
          let newFollowersThisWeek = 0;
          let newFollowersToday = 0;
          let followersWithEmailEnabled = 0;
          let followersWithPushEnabled = 0;
          const followersBySource: Record<string, number> = {
            manual: 0,
            ticket_purchase: 0,
            referral: 0,
            migration: 0,
          };

          snapshot.docs.forEach((doc) => {
            const data = doc.data() as OrganizerFollower;
            const followedAt = data.followedAt?.toDate?.() || new Date(data.followedAt as any);

            if (followedAt >= oneDayAgo) newFollowersToday++;
            if (followedAt >= oneWeekAgo) newFollowersThisWeek++;
            if (followedAt >= oneMonthAgo) newFollowersThisMonth++;
            if (followedAt >= twoMonthsAgo && followedAt < oneMonthAgo) newFollowersLastMonth++;

            if (data.notificationPreferences?.email) followersWithEmailEnabled++;
            if (data.notificationPreferences?.push) followersWithPushEnabled++;

            if (data.source) {
              followersBySource[data.source] = (followersBySource[data.source] || 0) + 1;
            }
          });

          // Calculate growth rate
          const growthRate =
            newFollowersLastMonth > 0
              ? ((newFollowersThisMonth - newFollowersLastMonth) / newFollowersLastMonth) * 100
              : newFollowersThisMonth > 0
              ? 100
              : 0;

          const stats: FollowerStats = {
            totalFollowers: snapshot.size,
            newFollowersThisMonth,
            newFollowersThisWeek,
            newFollowersToday,
            followersWithEmailEnabled,
            followersWithPushEnabled,
            followersBySource: {
              manual: followersBySource.manual || 0,
              ticket_purchase: followersBySource.ticket_purchase || 0,
              referral: followersBySource.referral || 0,
              migration: followersBySource.migration || 0,
            },
            growthRate,
          };

          return { data: stats };
        } catch (error: any) {
          console.error('Error fetching follower stats:', error);
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, organizerId) => [
        { type: 'FollowerStats', id: organizerId },
      ],
    }),

    // Get follower dashboard data (stats + recent followers)
    getFollowerDashboard: builder.query<FollowerDashboardData, string>({
      queryFn: async (organizerId) => {
        try {
          console.log('[FollowersAPI] Fetching dashboard for organizerId:', organizerId);

          // Fetch stats
          const followersRef = collection(DB, 'organisers', organizerId, 'followers');
          const snapshot = await getDocs(followersRef);

          console.log('[FollowersAPI] Found followers:', snapshot.size);

          const now = new Date();
          const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());

          let newFollowersThisMonth = 0;
          let newFollowersLastMonth = 0;
          let newFollowersThisWeek = 0;
          let newFollowersToday = 0;
          let followersWithEmailEnabled = 0;
          let followersWithPushEnabled = 0;
          const followersBySource: Record<string, number> = {
            manual: 0,
            ticket_purchase: 0,
            referral: 0,
            migration: 0,
          };

          const allFollowers: OrganizerFollower[] = [];

          snapshot.docs.forEach((doc) => {
            try {
              const data = doc.data() as OrganizerFollower;
              const follower = {
                ...data,
                userId: doc.id,
              };
              allFollowers.push(follower);

              const followedAt = data.followedAt?.toDate?.() || new Date(data.followedAt as any);

              if (followedAt >= oneDayAgo) newFollowersToday++;
              if (followedAt >= oneWeekAgo) newFollowersThisWeek++;
              if (followedAt >= oneMonthAgo) newFollowersThisMonth++;
              if (followedAt >= twoMonthsAgo && followedAt < oneMonthAgo) newFollowersLastMonth++;

              if (data.notificationPreferences?.email) followersWithEmailEnabled++;
              if (data.notificationPreferences?.push) followersWithPushEnabled++;

              if (data.source) {
                followersBySource[data.source] = (followersBySource[data.source] || 0) + 1;
              }
            } catch (err) {
              console.error('[FollowersAPI] Error processing follower document:', doc.id, err);
            }
          });

          // Calculate growth rate
          const growthRate =
            newFollowersLastMonth > 0
              ? ((newFollowersThisMonth - newFollowersLastMonth) / newFollowersLastMonth) * 100
              : newFollowersThisMonth > 0
              ? 100
              : 0;

          const stats: FollowerStats = {
            totalFollowers: snapshot.size,
            newFollowersThisMonth,
            newFollowersThisWeek,
            newFollowersToday,
            followersWithEmailEnabled,
            followersWithPushEnabled,
            followersBySource: {
              manual: followersBySource.manual || 0,
              ticket_purchase: followersBySource.ticket_purchase || 0,
              referral: followersBySource.referral || 0,
              migration: followersBySource.migration || 0,
            },
            growthRate,
          };

          // Get recent followers (last 10)
          const recentFollowers = allFollowers
            .sort((a, b) => {
              const dateA = a.followedAt?.toDate?.() || new Date(a.followedAt as any);
              const dateB = b.followedAt?.toDate?.() || new Date(b.followedAt as any);
              return dateB.getTime() - dateA.getTime();
            })
            .slice(0, 10);

          // Fetch user details for recent followers
          const recentFollowersWithUserData = await Promise.all(
            recentFollowers.map(async (follower) => {
              if (!follower.userDisplayName && follower.userId) {
                try {
                  const userRef = doc(DB, 'users', follower.userId);
                  const userDoc = await getDoc(userRef);
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    return {
                      ...follower,
                      userDisplayName: userData.displayName || userData.firstName || userData.email,
                      userEmail: userData.email,
                      userPhoto: userData.photoURL,
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user ${follower.userId}:`, error);
                }
              }
              return follower;
            })
          );

          console.log('[FollowersAPI] Dashboard data prepared:', {
            totalFollowers: stats.totalFollowers,
            recentFollowersCount: recentFollowersWithUserData.length,
          });

          return {
            data: {
              stats,
              recentFollowers: recentFollowersWithUserData,
            },
          };
        } catch (error: any) {
          console.error('[FollowersAPI] Error fetching follower dashboard:', error);
          console.error('[FollowersAPI] Error details:', {
            message: error.message,
            stack: error.stack,
            organizerId,
          });
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, organizerId) => [
        { type: 'Followers', id: organizerId },
        { type: 'FollowerStats', id: organizerId },
      ],
    }),
  }),
});

export const {
  useGetFollowersQuery,
  useGetFollowerStatsQuery,
  useGetFollowerDashboardQuery,
} = followersApi;
